import { useRef, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import FormHelperText from '@mui/material/FormHelperText';
import { Box, Stack, Tooltip, Typography } from '@mui/material';

import {
  AMUSE_MAX_IMAGE_SIZE,
  AMUSE_SUPPORTED_AUDIO_FORMATS,
  AMUSE_SUPPORTED_IMAGE_FORMATS,
} from 'src/utils/file-service';

import { Iconify } from '../iconify';
import { ImageCrop } from '../image-crop';
import { RHFTextField } from './rhf-text-field';
import { Upload, UploadBox, UploadAvatar } from '../upload';

import type { UploadProps } from '../upload';
import type { ThumbnailArea } from '../image-crop';

// ----------------------------------------------------------------------

type Props = UploadProps & {
  name: string;
};

// ----------------------------------------------------------------------

export function RHFUploadAvatar({ name, ...other }: Props) {
  const { control, setValue } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const onDrop = (acceptedFiles: File[]) => {
          const value = acceptedFiles[0];

          setValue(name, value, { shouldValidate: true });
        };

        return (
          <div>
            <UploadAvatar value={field.value} error={!!error} onDrop={onDrop} {...other} />

            {!!error && (
              <FormHelperText error sx={{ px: 2, textAlign: 'center' }}>
                {error.message}
              </FormHelperText>
            )}
          </div>
        );
      }}
    />
  );
}

// ----------------------------------------------------------------------

export function RHFUploadBox({ name, ...other }: Props) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <UploadBox value={field.value} error={!!error} {...other} />
      )}
    />
  );
}

// ----------------------------------------------------------------------

export function RHFUploadVideo({ name, multiple, helperText, ...other }: Props) {
  const { control, setValue } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const uploadProps = {
          multiple,
          error: !!error,
          helperText: error?.message ?? helperText,
        };

        const onDrop = (acceptedFiles: File[]) => {
          const value = multiple ? [...field.value, ...acceptedFiles] : acceptedFiles[0];

          setValue(name, value, { shouldValidate: true });
        };

        return (
          <Upload {...uploadProps} value={field.value} onDrop={onDrop} {...other} type="video" />
        );
      }}
    />
  );
}

// ----------------------------------------------------------------------

const getImageSource = (value: string | File) => {
  const source = value instanceof File ? URL.createObjectURL(value) : value;
  return source;
};

export function RHFUploadImage({
  imageFieldName,
  thumbnailAreaFieldName,
  helperText,
  cropAspect,
  disabled,
  altTextFieldName,
  disableCrop = false,
  ...other
}: UploadProps & {
  imageFieldName: string;
  thumbnailAreaFieldName: string;
  cropAspect?: number;
  disabled?: boolean;
  altTextFieldName: string;
  disableCrop?: boolean;
}) {
  const { control, setValue, watch } = useFormContext();
  const initialCroppedAreaRef = useRef<ThumbnailArea | null>(null);
  const image = watch(imageFieldName);

  const imageSource = useMemo(() => (image ? getImageSource(image) : null), [image]);

  return (
    <Stack spacing={2}>
      {!disableCrop ? (
        <Controller
          name={thumbnailAreaFieldName}
          control={control}
          render={({ field, fieldState: { error } }) => {
            const onCropComplete = (aoi: ThumbnailArea) => {
              setValue(thumbnailAreaFieldName, aoi, { shouldValidate: true, shouldDirty: true });
            };

            if (initialCroppedAreaRef.current === null) {
              initialCroppedAreaRef.current = field.value;
            }

            return (
              <>
                {imageSource && image instanceof File && (
                  <ImageCrop
                    image={imageSource}
                    aspect={cropAspect ?? 16 / 9}
                    onCropComplete={onCropComplete}
                    initialCroppedArea={initialCroppedAreaRef.current}
                    disabled={disabled}
                  />
                )}
              </>
            );
          }}
        />
      ) : (
        imageSource && (
          <Box
            component="img"
            src={imageSource}
            alt="Preview"
            sx={{
              width: '100%',
              height: 'auto',
              maxHeight: 400,
              objectFit: 'contain',
              borderRadius: 1,
            }}
          />
        )
      )}
      <Stack direction="row" spacing={2}>
        <Controller
          name={imageFieldName}
          control={control}
          render={({ field, fieldState: { error } }) => {
            const uploadProps = {
              accept: AMUSE_SUPPORTED_IMAGE_FORMATS,
              error: !!error,
              helperText: error?.message ?? helperText,
            };

            const onDrop = (acceptedFiles: File[]) => {
              const value = acceptedFiles[0];

              setValue(imageFieldName, value, { shouldValidate: true });

              if (disableCrop) {
                setValue(
                  thumbnailAreaFieldName,
                  { x1: 0, x2: 1, y1: 0, y2: 1 },
                  { shouldValidate: true }
                );
              }
            };

            return (
              <>
                {field.value ? (
                  <Stack spacing={1.5} overflow="hidden">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="subtitle2">Replace</Typography>
                      <Tooltip
                        title={`Maximum file size: ${AMUSE_MAX_IMAGE_SIZE / (1024 * 1024)}MB. Supported formats: JPG, PNG, WebP. Aspect ratio: 16:9, cropping allowed.`}
                      >
                        <Iconify icon="eva:info-outline" width={16} />
                      </Tooltip>
                    </Stack>
                    <UploadBox
                      {...uploadProps}
                      value={field.value}
                      onDrop={onDrop}
                      {...other}
                      disabled={disabled}
                    />
                  </Stack>
                ) : (
                  <Upload
                    {...uploadProps}
                    value={field.value}
                    onDrop={onDrop}
                    {...other}
                    disabled={disabled}
                    type="image"
                  />
                )}
              </>
            );
          }}
        />
        {image && (
          <Stack spacing={1.5}>
            <Typography variant="subtitle2">Alt Text</Typography>
            <RHFTextField
              name={altTextFieldName}
              placeholder="Describe this image..."
              disabled={disabled}
            />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}

export function RHFUploadAudio({ name, helperText, ...other }: Props) {
  const { control, setValue } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const uploadProps = {
          accept: AMUSE_SUPPORTED_AUDIO_FORMATS,
          error: !!error,
          helperText: error?.message ?? helperText,
        };

        const onDrop = (acceptedFiles: File[]) => {
          const value = acceptedFiles[0];

          setValue(name, value, { shouldValidate: true });
        };

        // TODO: Revoke the URL when the file is deleted
        // TODO: smarter handling of audio preview (currently created each render)
        return (
          <>
            {field.value ? (
              <Stack spacing={2}>
                <audio src={URL.createObjectURL(field.value)} controls>
                  <track kind="captions" />
                  Your browser does not support the audio element.
                </audio>
                <Stack spacing={1.5}>
                  <Typography variant="subtitle2">Replace</Typography>
                  <UploadBox {...uploadProps} value={field.value} onDrop={onDrop} {...other} />
                </Stack>
              </Stack>
            ) : (
              <Upload {...uploadProps} type="audio" value={field.value} onDrop={onDrop} {...other} />
            )}
          </>
        );
      }}
    />
  );
}
